import React, { useEffect } from "react";
import { AlertTriangle, CheckCircle2, X } from "lucide-react";

export function ConfirmationPopup({
    message,
    title,
    onSubmit,
    onCancel,
    closePopup,
    confirmLabel = "Continue",
    note
}) {

    const handleClose = () => {
        onCancel && onCancel();
        closePopup();
    };

    useEffect(() => {
        document.body.style.overflow = "hidden";

        const closeFromKeyboard = () => {
            onCancel && onCancel();
            closePopup();
        };

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                closeFromKeyboard();
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = "";
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [onCancel, closePopup]);

    const handleConfirm = () => {
        onSubmit && onSubmit();
        closePopup();
    };

    const renderFallbackMessage = () => (
        <>
            <p>This action will start as soon as you continue.</p>
            <p>Please confirm to proceed, or cancel to review everything one more time.</p>
        </>
    );

    const renderMessage = () => {
        if (typeof message === "string") {
            return /^are you sure\??$/i.test(message.trim()) ? renderFallbackMessage() : <p>{message}</p>;
        }

        if (React.isValidElement(message)) {
            const children = React.Children.toArray(message.props?.children);
            const firstChild = children[0];
            const firstChildText = React.isValidElement(firstChild) && typeof firstChild.props?.children === "string"
                ? firstChild.props.children.trim()
                : "";

            if (children.length > 1 && /^are you sure\??$/i.test(firstChildText)) {
                return children.slice(1);
            }

            if (children.length === 1 && /^are you sure\??$/i.test(firstChildText)) {
                return renderFallbackMessage();
            }
        }

        return message;
    };

    return (
        <div className="oct-remove-device-modal oct-remove-device-modal--primary" role="dialog" aria-modal="true" aria-labelledby="oct-shared-popup-title">
            <div className="oct-remove-device-modal__backdrop" onClick={handleClose} />

            <div className="oct-remove-device-modal__dialog" onClick={(event) => event.stopPropagation()}>
                <div className="oct-remove-device-modal__header">
                    <div className="oct-remove-device-modal__header-glow" />

                    <div className="oct-remove-device-modal__title-group">
                        <div className="oct-remove-device-modal__title-icon">
                            <AlertTriangle size={20} />
                        </div>

                        <h4 id="oct-shared-popup-title" className="oct-remove-device-modal__title">
                            {title}
                        </h4>
                    </div>

                    <button
                        type="button"
                        className="oct-remove-device-modal__close"
                        onClick={handleClose}
                        aria-label="Close popup"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="oct-remove-device-modal__body">
                    <div className="oct-remove-device-modal__warning-icon-wrap">
                        <div className="oct-remove-device-modal__warning-icon-shadow" />
                        <div className="oct-remove-device-modal__warning-icon">
                            <AlertTriangle size={38} strokeWidth={2.4} />
                        </div>
                    </div>

                    <div className="oct-remove-device-modal__content">
                        <h5 className="oct-remove-device-modal__heading">Are you sure?</h5>

                        <div className="oct-remove-device-modal__message">
                            {renderMessage()}
                        </div>
                    </div>

                    {note ? (
                        <div className="oct-remove-device-modal__note">
                            <AlertTriangle size={18} className="oct-remove-device-modal__note-icon" />
                            <p>{note}</p>
                        </div>
                    ) : null}
                </div>

                <div className="oct-remove-device-modal__footer">
                    <button
                        type="button"
                        className="btn oct-remove-device-modal__btn oct-remove-device-modal__btn--secondary"
                        onClick={handleClose}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="btn oct-remove-device-modal__btn oct-remove-device-modal__btn--primary"
                        onClick={handleConfirm}
                    >
                        <CheckCircle2 size={16} />
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
